"use client";

/**
 * SSH terminal (change fix-egress-and-ssh-terminal D1/D3/D6/D9).
 *
 * xterm.js paints what the API's asyncssh client gets from the PTY and sends
 * the input back. The bytes are the protocol: binary in both directions, JSON
 * only for `resize` (browser to API) and `ready`/`exit`/`error` (API to
 * browser). Nothing else travels, and the credential stays on the server
 * (SB-4).
 *
 * Clipboard is xterm's own selection plus the browser's own paste: there is no
 * guacd stream and no agent bridge (D3). Selecting writes to the PC clipboard
 * only when the user turns it on, and the content never lands in a log, an
 * audit event or a trace (SB-16).
 */

import type { FitAddon } from "@xterm/addon-fit";
import type { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";

import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import { setPreferredConnector } from "@/lib/connections";
import { listConnectors, type ConnectorOut } from "@/lib/connectors";
import {
  bytesFrom,
  capText,
  encodeResize,
  errorCodeKey,
  isLocalPaste,
  parseControl,
  readClipboard,
  themeFor,
  writeClipboard,
  type TerminalControl,
} from "@/lib/ssh-terminal";
import type { SessionTerminal } from "@/lib/terminal-prefs";

/**
 * xterm reads `self` while its own module is evaluated, so a static import in
 * a client component still runs during the server render and 500s the whole
 * route. The types above are erased at build time; the values are pulled in
 * only in the browser, and only once the socket that needs them exists.
 */
type XtermModules = {
  Terminal: typeof import("@xterm/xterm").Terminal;
  FitAddon: typeof import("@xterm/addon-fit").FitAddon;
  WebLinksAddon: typeof import("@xterm/addon-web-links").WebLinksAddon;
};

let xtermModules: Promise<XtermModules> | null = null;

function loadXterm(): Promise<XtermModules> {
  xtermModules ??= Promise.all([
    import("@xterm/xterm"),
    import("@xterm/addon-fit"),
    import("@xterm/addon-web-links"),
  ]).then(([core, fit, links]) => ({
    Terminal: core.Terminal,
    FitAddon: fit.FitAddon,
    WebLinksAddon: links.WebLinksAddon,
  }));
  return xtermModules;
}

export type TerminalTab = {
  sessionId: string;
  ticket: string;
  connectionId: string;
  connectionName: string;
  type: string;
  status: "connecting" | "open" | "closed" | "error";
  detail?: string;
  /**
   * Connector this session was pinned to, when the user chose one (D6/D9).
   * A session that opens on it proves the route works, which is the only
   * moment the preference is worth persisting.
   */
  viaConnectorId?: string;
};

type Props = {
  tab: TerminalTab;
  onStatus: (status: TerminalTab["status"], detail?: string) => void;
  onClose: () => void;
  /** Reopen the same connection the automatic way. */
  onRetry: () => void;
  /** Reopen the same connection pinned to `connector` (D9). */
  onRetryVia: (connector: ConnectorOut) => void;
};

type ClipNote = { text: string; kind: "copied" | "truncated" | "denied" };

const WS_BASE = process.env.NEXT_PUBLIC_WS_BASE ?? "";

function wsUrl(): string {
  const base = WS_BASE
    ? WS_BASE
    : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;
  return `${base.replace(/\/$/, "")}/api/ws/session`;
}

/** Bytes waiting for a terminal: `ready` is the first frame, but not by contract. */
const MAX_BUFFERED_BYTES = 64 * 1024;

/**
 * How long a selection has to stay still before it counts as complete. A drag
 * fires `onSelectionChange` on every pixel; copying each of them would write
 * the clipboard a dozen times for one gesture (D3: once per selection).
 */
const SELECTION_SETTLE_MS = 150;

export function SshTerminal({ tab, onStatus, onClose, onRetry, onRetryVia }: Props) {
  const t = useTranslations("sessions");
  const hostRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const statusRef = useRef(onStatus);
  const closeRef = useRef(onClose);
  const retryViaRef = useRef(onRetryVia);
  const tRef = useRef(t);
  // Keep the latest tab without reopening the socket on a parent rerender:
  // only a new sessionId/ticket means a new socket.
  const tabRef = useRef(tab);
  // Latest-ref assignments happen after commit (react-hooks/refs), before the
  // socket effect below reads them.
  useEffect(() => {
    statusRef.current = onStatus;
    closeRef.current = onClose;
    retryViaRef.current = onRetryVia;
    tRef.current = t;
    tabRef.current = tab;
  });

  const [clipboardOn, setClipboardOn] = useState(false);
  const [clipNote, setClipNote] = useState<ClipNote | null>(null);
  const [alternatives, setAlternatives] = useState<ConnectorOut[]>([]);
  const clipboardOnRef = useRef(clipboardOn);

  // PTY bytes can only be written once xterm exists, and xterm is built from
  // the preferences that arrive with `ready`. The buffer is a formality, not a
  // queue: `ready` always comes first and it is capped anyway.
  const bufferedRef = useRef<Uint8Array[]>([]);
  const endedCleanRef = useRef(false);
  const codeRef = useRef<number | null>(null);
  const detailRef = useRef<string | undefined>(undefined);
  const mountedRef = useRef(false);
  const attemptRef = useRef<{ key: string; opened: boolean } | null>(null);
  /** Teardown of the clipboard wiring, so it is never attached twice. */
  const clipCleanupRef = useRef<(() => void) | null>(null);
  /**
   * Re-wire hook handed over by the mount effect: it owns the socket closure
   * the clipboard listeners need, so the toggle can rebuild them without
   * touching (and therefore reissuing) the ticket.
   */
  const rewireRef = useRef<(() => void) | null>(null);
  const selectionTimerRef = useRef<number | null>(null);
  const lastCopiedRef = useRef("");

  /** Selection to PC clipboard, with the size cap and the truncation note. */
  const copySelection = useCallback(async () => {
    const term = termRef.current;
    if (!term) return;
    const selection = term.getSelection();
    if (!selection) return;
    const { text, truncated } = capText(selection);
    lastCopiedRef.current = selection;
    const ok = await writeClipboard(text);
    setClipNote({ text, kind: truncated ? "truncated" : ok ? "copied" : "denied" });
  }, [setClipNote]);

  // PC clipboard to terminal: text in, xterm turns it into input.
  const pasteClipboard = useCallback(async () => {
    const term = termRef.current;
    if (!term) return;
    const text = await readClipboard();
    // Empty or forbidden clipboard: nothing is injected and the session
    // stays exactly as it was.
    if (text === null) return;
    term.paste(capText(text).text);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    const cur = tabRef.current;
    if (!host) return;
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (cur.status !== "connecting") return;
    }
    if (cur.status !== "connecting") return;
    const key = `${cur.sessionId}:${cur.ticket}`;
    if (attemptRef.current?.key === key && attemptRef.current.opened) return;
    const ticket = cur.ticket.replace(/=+$/, "");
    const ws = new WebSocket(wsUrl(), [`vst-${ticket}`]);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    attemptRef.current = { key, opened: false };

    // A proxy that swallows the upgrade leaves the socket neither open nor
    // closed forever; say so instead of waiting for a reason that never comes.
    const openTimer = window.setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        try {
          ws.close();
        } catch {
          // already gone
        }
        detailRef.current = tRef.current("wsTimeout");
        statusRef.current("error", detailRef.current);
      }
    }, 15000);

    const build = async (prefs: SessionTerminal | null) => {
      const { Terminal: XtermTerminal, FitAddon: XtermFit, WebLinksAddon: XtermLinks } =
        await loadXterm();
      if (disposed) return null;
      const theme = themeFor(prefs);
      const term = new XtermTerminal({
        fontFamily: prefs?.font_name ?? "monospace",
        fontSize: prefs?.font_size ?? 12,
        cursorBlink: true,
        convertEol: false,
        scrollback: 5000,
        // Only the colours we actually resolved: passing a partial theme makes
        // xterm fill the rest with its defaults instead of ours.
        ...(Object.keys(theme).length > 0 ? { theme } : {}),
      });
      const fit = new XtermFit();
      term.loadAddon(fit);
      term.loadAddon(new XtermLinks());
      termRef.current = term;
      fitRef.current = fit;

      // Input: everything xterm produces goes back as UTF-8 bytes on one
      // channel — keystrokes, and the mouse reports the remote app asks for
      // (vim, tmux, top) once it enables tracking. Full capture: xterm's
      // textarea helper consumes the browser's key handling, so Backspace never
      // navigates back and Space never scrolls the page.
      const encoder = new TextEncoder();
      term.onData((data) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(encoder.encode(data));
      });

      term.open(host);
      applyGeometry(fit, ws, term, host);

      for (const chunk of bufferedRef.current.splice(0)) term.write(chunk);
      wireClipboard(term);
      return term;
    };

    const pushGeometry = () => {
      const fit = fitRef.current;
      const term = termRef.current;
      if (!fit || !term) return;
      applyGeometry(fit, ws, term, host);
    };

    /**
     * Attach or detach the clipboard wiring. Called on toggle and again when
     * xterm is built (it may arrive after the user already turned it on), so
     * the toggle never reopens the socket: a ticket is single-use.
     */
    const wireClipboard = (term: Terminal) => {
      const hostEl = host;
      clipCleanupRef.current?.();
      clipCleanupRef.current = null;
      lastCopiedRef.current = "";
      if (!clipboardOnRef.current) return;

      // xterm's textarea preventDefaults every key it handles, so a shortcut
      // paste never fires a `paste` event on the host. Capture on document, and
      // only while this view has focus.
      const capturePaste = (ev: KeyboardEvent) => {
        if (!isLocalPaste(ev)) return;
        if (!hostEl.contains(ev.target as Node)) return;
        ev.preventDefault();
        ev.stopPropagation();
        void pasteClipboard();
      };
      const onPaste = (ev: ClipboardEvent) => {
        const text = ev.clipboardData?.getData("text/plain");
        if (text) term.paste(capText(text).text);
        else void pasteClipboard();
      };
      // Opt-in: selecting text never writes the PC clipboard on its own, and a
      // selection in progress is copied once, when it settles.
      const onSelectionChange = () => {
        const selection = term.getSelection();
        if (selectionTimerRef.current !== null) window.clearTimeout(selectionTimerRef.current);
        selectionTimerRef.current = null;
        if (!selection) {
          lastCopiedRef.current = "";
          return;
        }
        selectionTimerRef.current = window.setTimeout(() => {
          selectionTimerRef.current = null;
          const final = term.getSelection();
          if (!final || final === lastCopiedRef.current) return;
          lastCopiedRef.current = final;
          const { text, truncated } = capText(final);
          void writeClipboard(text).then((ok) =>
            setClipNote({ text, kind: truncated ? "truncated" : ok ? "copied" : "denied" }),
          );
        }, SELECTION_SETTLE_MS);
      };

      document.addEventListener("keydown", capturePaste, true);
      hostEl.addEventListener("paste", onPaste as EventListener);
      const sub = term.onSelectionChange(onSelectionChange);
      clipCleanupRef.current = () => {
        document.removeEventListener("keydown", capturePaste, true);
        hostEl.removeEventListener("paste", onPaste as EventListener);
        sub.dispose();
        if (selectionTimerRef.current !== null) window.clearTimeout(selectionTimerRef.current);
        selectionTimerRef.current = null;
      };
    };

    rewireRef.current = () => {
      const term = termRef.current;
      if (term) wireClipboard(term);
    };

    // Set by the cleanup below. A `close()` on a socket that is being torn
    // down still fires `onclose`, and reporting that would mark the new tab
    // (or a tab that is already gone) as failed.
    let disposed = false;

    ws.onopen = () => {
      if (disposed) return;
      window.clearTimeout(openTimer);
      if (attemptRef.current?.key === key) attemptRef.current.opened = true;
    };

    ws.onmessage = (ev: MessageEvent) => {
      if (typeof ev.data === "string") {
        const control = parseControl(ev.data);
        if (!control) return; // unknown control frame: ignore, keep the PTY
        if (control.type === "ready") {
          void build(control.terminal).then(() => {
            if (disposed) return;
            // The PTY geometry the server really opened is the truth; xterm is
            // fitted to the container and told about it from now on.
            pushGeometry();
            statusRef.current("open");
            // A session pinned to a connector that actually opened proves the
            // route works, so it earns the connection's preference (D9). Only
            // now: an attempt that failed says nothing about the route.
            if (tabRef.current.viaConnectorId) {
              void setPreferredConnector(
                tabRef.current.connectionId,
                tabRef.current.viaConnectorId,
              ).catch(() => undefined);
            }
            termRef.current?.focus();
          });
        } else if (control.type === "exit") {
          endedCleanRef.current = true;
          closeRef.current();
        } else if (control.type === "error") {
          codeRef.current = control.code;
          statusRef.current("error", tRef.current(errorCodeKey(control.code) ?? "wsError.1011"));
        }
        return;
      }
      const bytes = bytesFrom(ev.data);
      if (!bytes) return;
      const term = termRef.current;
      if (term) {
        term.write(bytes);
        return;
      }
      bufferedRef.current.push(bytes);
      const size = bufferedRef.current.reduce((n, chunk) => n + chunk.length, 0);
      if (size > MAX_BUFFERED_BYTES) {
        // Better a truncated banner than an unbounded buffer on a frame flood.
        bufferedRef.current = [bufferedRef.current.shift() as Uint8Array];
      }
    };

    ws.onclose = (ev: CloseEvent) => {
      if (disposed) return;
      // `exit` already closed the tab; nothing left to say.
      if (endedCleanRef.current) return;
      const code = codeRef.current ?? ev.code;
      const key2 = errorCodeKey(code);
      const detail = key2 ? tRef.current(key2) : detailRef.current;
      statusRef.current(detail ? "error" : "closed", detail);
    };

    const ro =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            pushGeometry();
          });
    ro?.observe(host);

    return () => {
      disposed = true;
      window.clearTimeout(openTimer);
      ro?.disconnect();
      rewireRef.current = null;
      clipCleanupRef.current?.();
      clipCleanupRef.current = null;
      termRef.current?.dispose();
      termRef.current = null;
      fitRef.current = null;
      wsRef.current = null;
      if (!attemptRef.current?.opened) attemptRef.current = null;
      try {
        ws.close();
      } catch {
        // already gone
      }
    };
    // `wireClipboard` closes over this effect's socket, so it is intentionally
    // not a dependency: the toggle reaches it through `clipboardOnRef`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.sessionId, tab.ticket]);

  // Toggling the sync re-wires the clipboard listeners only. It must never
  // reopen the WS (the ticket is single-use), hence a separate effect.
  useEffect(() => {
    clipboardOnRef.current = clipboardOn;
    rewireRef.current?.();
  }, [clipboardOn]);

  // Alternatives for the "connect another way" action (D9): only meaningful
  // once the session failed, only connectors that can carry traffic right now,
  // and never the one that just failed.
  useEffect(() => {
    if (tab.status !== "error") return;
    let alive = true;
    void listConnectors()
      .then((list) => {
        if (!alive) return;
        setAlternatives(
          list.filter(
            (c) => c.online && c.id !== (tab.viaConnectorId ?? ""),
          ),
        );
      })
      .catch(() => {
        if (alive) setAlternatives([]);
      });
    return () => {
      alive = false;
    };
  }, [tab.status, tab.viaConnectorId]);

  return (
    <div
      data-session-stage
      className="relative max-h-[calc(100vh-6rem)] min-h-[260px] bg-slate-100 dark:bg-slate-950"
      style={{ height: "var(--session-pane-height, 70vh)" }}
    >
      <div
        ref={hostRef}
        data-testid={`session-host-${tab.sessionId}`}
        className="absolute inset-0 z-0 overflow-hidden p-2 focus:outline-none"
      />
      {tab.status === "connecting" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm text-slate-400">{t("connecting")}</span>
        </div>
      )}
      {(tab.status === "error" || tab.status === "closed") && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/95 p-6 text-center dark:bg-slate-900/95">
          <p className="max-w-sm text-sm font-medium text-slate-700 dark:text-slate-200">
            {tab.status === "error" ? (tab.detail ?? t("wsError.1011")) : t("closed")}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-slate-100 dark:text-slate-900"
            >
              {t("retry")}
            </button>
            {alternatives.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`via-${c.id}`}
                onClick={() => retryViaRef.current(c)}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700"
              >
                {t("tryOtherEgress", { name: c.name })}
              </button>
            ))}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700"
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}
      {tab.status === "open" && (
        <>
          <div className="absolute right-2 top-2 z-20 flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                const next = !clipboardOn;
                setClipboardOn(next);
                if (!next) setClipNote(null);
              }}
              aria-pressed={clipboardOn}
              title={clipboardOn ? t("clipboard.disable") : t("clipboard.enable")}
              className={
                "rounded-md px-2 py-1 text-xs font-medium " +
                (clipboardOn
                  ? "bg-slate-900/80 text-white dark:bg-slate-100/90 dark:text-slate-900"
                  : "bg-slate-200/80 text-slate-700 dark:bg-slate-800/90 dark:text-slate-300")
              }
            >
              {t("clipboard.toggle")}
            </button>
            <button
              type="button"
              onClick={() => void pasteClipboard()}
              title={t("clipboard.paste")}
              className="rounded-md bg-slate-900/80 px-2 py-1 text-xs font-medium text-white dark:bg-slate-100/90 dark:text-slate-900"
            >
              {t("clipboard.paste")}
            </button>
            <button
              type="button"
              onClick={() => void copySelection()}
              title={t("clipboard.copySelection")}
              className="rounded-md bg-slate-900/80 px-2 py-1 text-xs font-medium text-white dark:bg-slate-100/90 dark:text-slate-900"
            >
              {t("clipboard.copySelection")}
            </button>
          </div>
          {clipNote && (
            <div className="absolute inset-x-2 bottom-2 z-20 flex items-center gap-2 rounded-md bg-slate-900/90 px-3 py-2 text-xs text-white dark:bg-slate-100/95 dark:text-slate-900">
              <span className="shrink-0">{t(`clipboard.note.${clipNote.kind}`)}</span>
              <code className="min-w-0 flex-1 truncate opacity-80">{clipNote.text}</code>
              <button
                type="button"
                onClick={() => void writeClipboard(clipNote.text)}
                className="shrink-0 underline underline-offset-2"
              >
                {t("clipboard.copy")}
              </button>
              <button
                type="button"
                onClick={() => setClipNote(null)}
                className="shrink-0 opacity-70"
              >
                {t("clipboard.dismiss")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Fit xterm to the container and tell the PTY. The PTY lives in character
 * cells, so this is the only place where pixels become `cols`/`rows`.
 */
function applyGeometry(
  fit: FitAddon,
  ws: WebSocket,
  term: Terminal,
  host: HTMLElement,
): void {
  if (ws.readyState !== WebSocket.OPEN) return;
  // Hidden stage (the bar keeps the socket alive off-host routes): a zero-size
  // fit would collapse the terminal to its minimum.
  if (host.clientWidth === 0 || host.clientHeight === 0) return;
  try {
    fit.fit();
  } catch {
    return; // not laid out yet
  }
  ws.send(encodeResize(term.cols, term.rows));
}

export type { TerminalControl };